/**
 * Shared config — canvas, fonts, back button dims, countdown.
 * Plain JS, no schema. Consumed via `window.CONFIG` and the SkinResolver.
 */

/**
 * Cross-game settings — exposed under the pseudo-game id `shared` so the
 * existing {@link SkinResolver} read/write API works unchanged.
 *
 * Visual filter toggles (CRT, Old Film, Pixelate, Tilt-Shift, God Rays)
 * live here because they apply to every game's stage uniformly. The
 * shaders themselves live in `src/systems/GameFilters.js`.
 *
 * All filters default to OFF — they're a stylistic opt-in, and stacking
 * several at once is a real GPU cost on low-end devices.
 */
export const SHARED_GAME = {
    settings: {
        // ── General toggles ────────────────────────────────────────
        // Cross-game UX preferences. `leaderboardsEnabled` gates the
        // entire high-score subsystem: when off, the Score Submit
        // overlay skips itself, the SceneChrome trophy button is
        // hidden, and the per-game leaderboard buttons on the menu
        // are suppressed. Defaults ON so existing players keep their
        // current behaviour after the upgrade.
        leaderboardsEnabled: { default: false, type: 'boolean', label: 'Leaderboards' },

        // Language selector mode for the main menu's HU/EN pill.
        //   'both' — Both languages selectable, pill visible (default).
        //   'hu'   — Force Hungarian, pill hidden, no toggle code runs.
        //   'en'   — Force English,   pill hidden, no toggle code runs.
        // Hosts deploying to a single-language audience can pick one
        // and skip the whole language-switch UI without touching code.
        availableLanguages:  { default: 'both', type: 'choice', options: ['both', 'hu', 'en'], label: 'Available Languages' },

        // Particle Quality — device performance tier. Caps how many
        // particles ANY single emitter may keep alive at once, so heavy
        // effects (snow buildup, long-lived streams) can't blow the frame
        // budget on weak hardware. The particle pool is lazy, so this is a
        // SAFETY ceiling, not a target: low-end devices stay at 60fps, beefy
        // ones get denser effects. Tiers map to caps in ParticleSystem.js
        // (low 2000 · medium 4000 · high 6000 · ultra 10000). Applied at
        // boot (src/main.js) and live from the Settings screen.
        particleQuality:     { default: 'high', type: 'choice', options: ['low', 'medium', 'high', 'ultra'], label: 'Particle Quality' },

        // ── Filter master toggles ──────────────────────────────────
        crtFilter:        { default: false, type: 'boolean', label: 'CRT Filter' },
        oldFilmFilter:    { default: false, type: 'boolean', label: 'Old Film Filter' },
        pixelateFilter:   { default: false, type: 'boolean', label: 'Pixelate Filter' },
        tiltShiftFilter:  { default: false, type: 'boolean', label: 'Tilt-Shift Filter' },
        godRaysFilter:    { default: false, type: 'boolean', label: 'God Rays Filter' },
        adjustmentFilter: { default: false, type: 'boolean', label: 'Adjustment Filter' },
        reflectionFilter: { default: false, type: 'boolean', label: 'Reflection Filter' },
        greyscaleFilter:  { default: false, type: 'boolean', label: 'Greyscale Filter' },

        // ── CRT customisation ──────────────────────────────────────
        // Defaults match the previous hard-coded uniforms in
        // GameFilters.js so existing screenshots stay identical, plus
        // the extended pixi-filters CRT param set (lineWidth, noise,
        // noiseSize, verticalLine, seed, vignetteAlpha) for finer
        // control. crtAnimating freezes the scanline roll + noise
        // shuffle when off — useful for static screenshots.
        crtAnimating:        { default: true, type: 'boolean', label: 'Animating' },
        crtVerticalLine:     { default: false,type: 'boolean', label: 'Vertical Lines' },
        crtCurvature:        { default: 0.06, type: 'number', min: 0,    max: 0.30, step: 0.01, label: 'Curvature' },
        crtScanline:         { default: 0.55, type: 'number', min: 0,    max: 1.00, step: 0.05, label: 'Line Contrast' },
        crtLineWidth:        { default: 1.4,  type: 'number', min: 0.4,  max: 8.00, step: 0.10, label: 'Line Width' },
        crtChroma:           { default: 1.4,  type: 'number', min: 0,    max: 5.00, step: 0.10, label: 'Chromatic Aberration' },
        crtNoise:            { default: 0.0,  type: 'number', min: 0,    max: 1.00, step: 0.02, label: 'Noise' },
        crtNoiseSize:        { default: 1.0,  type: 'number', min: 0.5,  max: 6.00, step: 0.10, label: 'Noise Size' },
        crtVignette:         { default: 0.45, type: 'number', min: 0,    max: 1.50, step: 0.05, label: 'Vignette' },
        crtVignetteAlpha:    { default: 1.0,  type: 'number', min: 0,    max: 1.00, step: 0.05, label: 'Vignette Alpha' },
        crtSeed:             { default: 0.5,  type: 'number', min: 0,    max: 1.00, step: 0.01, label: 'Seed' },

        // ── Old Film customisation ─────────────────────────────────
        oldFilmAnimating:    { default: true, type: 'boolean', label: 'Animating' },
        oldFilmSepia:        { default: 0.85, type: 'number', min: 0,    max: 1.00, step: 0.05, label: 'Sepia' },
        oldFilmNoise:        { default: 0.18, type: 'number', min: 0,    max: 0.80, step: 0.02, label: 'Grain' },
        oldFilmScratch:      { default: 0.18, type: 'number', min: 0,    max: 1.00, step: 0.05, label: 'Scratches' },
        oldFilmVignette:     { default: 0.55, type: 'number', min: 0,    max: 1.50, step: 0.05, label: 'Vignette' },

        // ── Pixelate customisation ─────────────────────────────────
        pixelateSize:        { default: 6,    type: 'number', min: 1,    max: 32,   step: 1,    label: 'Pixel Size' },

        // ── Tilt-Shift customisation ───────────────────────────────
        // (Focus position is auto-tracked to the player; only the
        // band geometry + blur strength are user-controlled.)
        tiltShiftBand:       { default: 0.18, type: 'number', min: 0,    max: 0.80, step: 0.02, label: 'Sharp Band' },
        tiltShiftRange:      { default: 0.50, type: 'number', min: 0.05, max: 1.00, step: 0.05, label: 'Falloff Range' },
        tiltShiftBlur:       { default: 7.0,  type: 'number', min: 0,    max: 24,   step: 0.5,  label: 'Max Blur' },

        // ── God Rays customisation ─────────────────────────────────
        godRaysAnimating:    { default: true, type: 'boolean', label: 'Animating' },
        godRaysIntensity:    { default: 0.6,  type: 'number', min: 0,    max: 2.00, step: 0.05, label: 'Intensity' },
        godRaysExposure:     { default: 0.5,  type: 'number', min: 0,    max: 1.50, step: 0.05, label: 'Exposure' },
        godRaysDecay:        { default: 0.965,type: 'number', min: 0.85, max: 0.999,step: 0.005,label: 'Decay' },
        godRaysLightX:       { default: 0.85, type: 'number', min: -0.5, max: 1.50, step: 0.05, label: 'Light X' },
        godRaysLightY:       { default: -0.05,type: 'number', min: -0.5, max: 1.50, step: 0.05, label: 'Light Y' },

        // ── Adjustment customisation ───────────────────────────────
        // Colour-grading knobs. All multipliers default to 1.0 (no
        // change). Gamma >1 brightens shadows; saturation 0 is full
        // greyscale; contrast 0 collapses everything to mid-grey.
        adjustGamma:         { default: 1.0,  type: 'number', min: 0.1, max: 3.0, step: 0.05, label: 'Gamma' },
        adjustSaturation:    { default: 1.0,  type: 'number', min: 0,   max: 3.0, step: 0.05, label: 'Saturation' },
        adjustContrast:      { default: 1.0,  type: 'number', min: 0,   max: 3.0, step: 0.05, label: 'Contrast' },
        adjustBrightness:    { default: 1.0,  type: 'number', min: 0,   max: 3.0, step: 0.05, label: 'Brightness' },
        adjustRed:           { default: 1.0,  type: 'number', min: 0,   max: 3.0, step: 0.05, label: 'Red' },
        adjustGreen:         { default: 1.0,  type: 'number', min: 0,   max: 3.0, step: 0.05, label: 'Green' },
        adjustBlue:          { default: 1.0,  type: 'number', min: 0,   max: 3.0, step: 0.05, label: 'Blue' },

        // ── Reflection customisation ───────────────────────────────
        // Boundary 0..1 = vertical position where the reflection
        // starts (0.7 = bottom 30% mirrors the upper portion).
        // Amplitude is in pixels of horizontal sway, waveLength is
        // the number of cycles down the reflected band.
        reflectionAnimating: { default: true, type: 'boolean', label: 'Animating' },
        reflectBoundary:     { default: 0.70, type: 'number', min: 0.10, max: 0.95, step: 0.02, label: 'Boundary' },
        reflectAmplitude:    { default: 8.0,  type: 'number', min: 0,    max: 40,   step: 0.5,  label: 'Wave Amplitude' },
        reflectWaveLength:   { default: 8.0,  type: 'number', min: 1,    max: 30,   step: 0.5,  label: 'Wave Frequency' },
        reflectAlpha:        { default: 0.55, type: 'number', min: 0,    max: 1.00, step: 0.05, label: 'Reflection Opacity' },
        reflectSpeed:        { default: 1.0,  type: 'number', min: 0,    max: 5.00, step: 0.10, label: 'Wave Speed' },

        // ── Greyscale customisation ────────────────────────────────
        // 0 = full colour, 1 = full monochrome. Tinted slightly
        // toward the configured tint colour for an "ink wash" feel
        // when the user nudges the warm/cool hue sliders below.
        greyscaleAmount:     { default: 1.0,  type: 'number', min: 0,    max: 1.00, step: 0.05, label: 'Amount' },
        greyscaleTintR:      { default: 1.0,  type: 'number', min: 0,    max: 2.00, step: 0.05, label: 'Tint Red' },
        greyscaleTintG:      { default: 1.0,  type: 'number', min: 0,    max: 2.00, step: 0.05, label: 'Tint Green' },
        greyscaleTintB:      { default: 1.0,  type: 'number', min: 0,    max: 2.00, step: 0.05, label: 'Tint Blue' },
    },
    elements: {},
};

export const SHARED_CONFIG = {
    canvas: {
        width: 1920,
        height: 1080,
        backgroundColor: 0x0b0f17,
        antialias: true,
    },

    fonts: {
        family: 'Inter, system-ui, -apple-system, sans-serif',
        hudSize: 28,
        hudColor: 0xffffff,
        titleSize: 64,
        titleColor: 0xffffff,
    },

    backButton: {
        width: 120,
        height: 48,
        radius: 12,
        offsetX: 24,
        offsetY: 24,
        labelKey: 'common.back',
        bg: 0x1f2937,
        bgHover: 0x374151,
        label: 0xffffff,
    },

    countdown: {
        durationFrames: 180,
        labelSize: 144,
        labelColor: 0xfacc15,
    },

    hud: {
        panel: {
            paddingX: 24,
            paddingY: 12,
            radius: 12,
            bg: 0x111827,
            bgAlpha: 0.78,
            stroke: 0x1f2937,
            strokeWidth: 2,
        },
    },

    /**
     * Win-screen confetti tuning. Consumed by src/ui/Confetti.js.
     *
     *   bursts        — how many separate explosions
     *   delayMs       — gap between bursts (ms)
     *   count         — particles per burst
     *   size          — particle size multiplier
     *   velocity      — initial particle velocity (px/s)
     *   fade          — true = fade out, false = fall off-screen
     *   positions     — relative origin per burst, in 0..1 of viewport.
     *                   Length should match `bursts` (extras are ignored).
     *                   Default = centre, then alternating left/right.
     */
    confetti: {
        bursts: 3,
        delayMs: 220,
        count: 140,
        size: 1.7,
        velocity: 280,
        fade: false,
        positions: [
            { x: 0.50, y: 0.55 },
            { x: 0.25, y: 0.40 },
            { x: 0.75, y: 0.40 },
        ],
        /**
         * Rain shower fired after the burst sequence (1 sweep, evenly spaced
         * columns across the top edge).
         */
        rain: {
            columns: 7,
            columnDelayMs: 70,
            count: 90,
            size: 1.6,
            velocity: 380,
            fade: false,
        },
    },
};
